import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { People } from '../../hooks/api/useExperiences';
import { Availability } from '../../hooks/api/useCalls';
import { PeerListenerTile } from '../peer-listener-tile';
import { SchedulingModal } from '../modals/scheduling-modal/scheduling-modal';
import { ROUTE_PATH } from '../../routes/route-paths';
import { useMediaQuery } from '../../hooks/utils/useMediaQuery';

interface FiListenerResultsProps {
  items: Record<string, unknown>[];
  searchQuery: string;
  /** Optional hook; must not close/unmount FiPanel — SchedulingModal is a child of this component. */
  onScheduleOpen?: () => void;
}

function asPeople(raw: Record<string, unknown>): People | null {
  const lr = raw.listener_role_id;
  const user = raw.user as People['user'] | undefined;
  if (typeof lr !== 'number' || !user?.display_name) return null;
  return {
    ...(raw as unknown as People),
    score: typeof raw.score === 'number' ? raw.score : 0,
  };
}

function CarouselNavChevron({ dir }: { dir: 'left' | 'right' }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden className="text-gray-800">
      {dir === 'left' ? (
        <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      ) : (
        <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      )}
    </svg>
  );
}

export const FiListenerResults: React.FC<FiListenerResultsProps> = ({ items, searchQuery, onScheduleOpen }) => {
  const history = useHistory();
  const isMdUp = useMediaQuery('md');
  const scrollRef = useRef<HTMLDivElement>(null);
  const [schedulePeer, setSchedulePeer] = useState<People | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const showNav = isMdUp && items.length >= 3;

  const seeAll = () => {
    const q = encodeURIComponent(searchQuery || '');
    history.push(`${ROUTE_PATH.BROWSE_PEER_LISTENERS}?search=${q}`);
  };

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const left = el.scrollLeft;
    const max = el.scrollWidth - el.clientWidth;
    setCanScrollLeft(left > 0);
    setCanScrollRight(left < max - 1);
  }, []);

  const scrollBy = (delta: number) => {
    scrollRef.current?.scrollBy({ left: delta, behavior: 'smooth' });
    window.requestAnimationFrame(updateScrollState);
  };

  useEffect(() => {
    updateScrollState();
  }, [items.length, updateScrollState]);

  return (
    <div className="bg-button-secondary w-full rounded-[20px] px-5 py-[18px]">
      <div className="mb-3 flex min-h-[36px] items-center justify-between gap-6">
        <p className="font-albert-sans shrink-0 text-xl font-semibold leading-[1.25] text-black">Listeners</p>
        {showNav ? (
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              aria-label="Scroll left"
              onClick={() => scrollBy(-200)}
              disabled={!canScrollLeft}
              className="flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 shadow-[0px_1px_0.5px_rgba(29,41,61,0.02)] hover:bg-gray-50 disabled:pointer-events-none disabled:opacity-40"
            >
              <CarouselNavChevron dir="left" />
            </button>
            <button
              type="button"
              aria-label="Scroll right"
              onClick={() => scrollBy(200)}
              disabled={!canScrollRight}
              className="flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 shadow-[0px_1px_0.5px_rgba(29,41,61,0.02)] hover:bg-gray-50 disabled:pointer-events-none disabled:opacity-40"
            >
              <CarouselNavChevron dir="right" />
            </button>
          </div>
        ) : null}
      </div>
      <div
        ref={scrollRef}
        onScroll={updateScrollState}
        className="flex gap-5 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {items.map((raw, i) => {
          const peer = asPeople(raw);
          if (!peer) return null;
          return (
            <div key={peer.listener_role_id ?? i} className="shrink-0">
              <PeerListenerTile
                peer={peer}
                onScheduleClick={(p) => {
                  onScheduleOpen?.();
                  setSchedulePeer(p);
                }}
              />
            </div>
          );
        })}
      </div>
      <div className="mt-6 flex justify-center">
        <button
          type="button"
          onClick={seeAll}
          className="font-albert-sans border-input-border-gray text-primary w-full max-w-[346px] rounded-full border bg-white px-7 py-2.5 text-base font-medium leading-5 shadow-[0px_1px_0.5px_rgba(29,41,61,0.02)] transition-colors hover:bg-gray-50"
        >
          See more results like this
        </button>
      </div>

      {schedulePeer && (
        <SchedulingModal
          open
          onExit={() => setSchedulePeer(null)}
          listenerId={schedulePeer.listener_role_id}
          listenerAvailableNow={schedulePeer.is_available}
          availabilities={(schedulePeer.availabilities ?? []) as Availability[]}
          listenerName={schedulePeer.user.display_name}
          listenerImage={schedulePeer.profile_photo_url_square}
          location="fi_chat"
        />
      )}
    </div>
  );
};
